// Budget Optimiser — the PRD's six sub-features under one plan.
//
// One principle: nothing is suggested that is expected to lose money, and
// nothing risks more than it expects to gain. Every change is sized from the
// seller's own hours and days, never from a multiplier somebody picked.
//
// Reverts, then stock, then events, then the monthly ceiling, then moving money,
// then cutting, then adding. One change per campaign at a time.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiArrowLeft, FiLink } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './BudgetOptimiser.module.css';
import AuthContext from '../../../context/AuthContext';
import FeatureCards from './components/FeatureCards';
import Actions from './components/Actions';
import Envelope from './components/Envelope';
import Settings from './components/Settings';
import Changes from '../KeywordOptimiser/components/Changes';
import { NotConnectedError, applyOne, approveMany, decide, getChanges, getPlan,
         getSettings, getWatch, saveSettings, undoOne } from './api';

const TABS = [
  { id: 'plan', label: "Today's plan", ref: '' },
  { id: 'rescue', label: 'Switching off early', ref: 'SF1' },
  { id: 'reallocate', label: 'Move money', ref: 'SF2' },
  { id: 'planner', label: 'The month', ref: 'SF3' },
  { id: 'cuts', label: 'Cap losses', ref: 'SF4' },
  { id: 'stock', label: 'Stock', ref: 'SF5' },
  { id: 'events', label: 'Events', ref: 'SF6' },
  { id: 'changes', label: 'Change log', ref: '' },
  { id: 'settings', label: 'Your budget', ref: '' },
];
const OWNER_OF_TAB = { rescue: 'rescue', reallocate: 'reallocate', cuts: 'cut',
                       stock: 'stock', events: 'event' };

export default function BudgetOptimiser() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);

  const [tab, setTab] = useState('plan');
  const [plan, setPlan] = useState(null);
  const [changes, setChanges] = useState(null);
  const [settings, setSettings] = useState(null);
  const [watch, setWatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [notConnected, setNotConnected] = useState(null);
  const [reload, setReload] = useState(0);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(e.message);
    else setError(e.message);
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let live = true;
    setLoading(true);
    getPlan(token)
      .then((r) => { if (live) { setPlan(r); setNotConnected(null); setError(null); } })
      .catch((e) => { if (live) fail(e); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [token, fail, reload]);

  useEffect(() => {
    if (!token || tab !== 'changes') return;
    getChanges(token, {}).then((r) => setChanges(r.changes)).catch(fail);
  }, [token, tab, fail, reload]);

  useEffect(() => {
    if (!token || tab !== 'settings') return;
    getSettings(token).then(setSettings).catch(fail);
  }, [token, tab, fail, reload]);

  useEffect(() => {
    if (!token || tab !== 'events') return;
    getWatch(token).then(setWatch).catch(() => {});
  }, [token, tab, reload]);

  const onDecide = async (action, decision) => {
    setBusy(true); setNotice(null);
    try {
      const r = await decide(token, action.id, decision);
      setNotice(decision === 'approve' ? (r.apply_note || 'Approved.')
        : decision === 'dismiss' ? 'Set aside.' : 'Back on the plan.');
      setReload((n) => n + 1);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onApproveMany = async (ids) => {
    setBusy(true); setNotice(null);
    try {
      const r = await approveMany(token, ids);
      setNotice([`${r.total} approved.`, r.linked_note, r.apply_note]
        .filter(Boolean).join(' '));
      setReload((n) => n + 1);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onApply = async (action) => {
    setBusy(true); setNotice(null);
    try {
      const r = await applyOne(token, action.id, true);
      const what = (r.steps || []).map((step) =>
        `${step.method || 'here'} ${step.path || ''} — ${step.what}`).join(' · ');
      setNotice(`${r.reason} ${what}`.trim());
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onUndo = async (idOrAction) => {
    const id = typeof idOrAction === 'string' ? idOrAction : idOrAction.id;
    setBusy(true); setNotice(null);
    try {
      const r = await undoOne(token, id, true);
      setNotice(r.reason);
      setReload((n) => n + 1);
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const onSaveSettings = async (patch) => {
    setBusy(true);
    try { setSettings(await saveSettings(token, patch)); setReload((n) => n + 1); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };

  const listFor = (owner) => (!plan ? null : {
    ...plan,
    actions: (plan.actions || []).filter((a) => a.owner === owner),
    dropped: (plan.dropped || []).filter((a) => a.owner === owner),
  });

  function body() {
    if (notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Amazon Ads is not connected</h2>
          <p className={s.lockedBody}>
            {notConnected} Advertising is a second Amazon authorisation, separate from
            the Selling Partner one — connect it on the Amazon card and collect the
            last sixty days.
          </p>
          <button className={s.button}
                  onClick={() => navigate('/dashboard/market-places')}>
            Go to the Amazon card
          </button>
        </div>
      );
    }

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}
        {notice && <div className={s.notice}>{notice}</div>}

        {plan && (
          <section className={s.panel}>
            <div className={s.contextBar}>
              <div className={s.coverage}>
                Target ACOS {(plan.target_acos * 100).toFixed(0)}%
                {plan.target_source === 'benchmark'
                  ? ' (a benchmark, not a target you set)' : ''}.
                {' '}The order is: {(plan.priority_order || []).join(' → ')}.
              </div>
            </div>
            {plan.freshness?.stale && (
              <div className={s.coverageWarn}>
                <FiAlertTriangle /> {plan.freshness.why}
              </div>
            )}
            {plan.usage_basis !== 'marketing stream budget usage'
              && plan.usage_note && (
              <div className={s.coverageWarn}>{plan.usage_note}</div>
            )}
            {!!(plan.steps || []).length && (
              <div className={s.stepList}>
                {(plan.steps || []).map((step) => (
                  <span key={step.step}
                        className={`${s.step} ${step.count ? s.step_acted : ''}`}>
                    {step.step}. {step.owner}{step.count ? ` · ${step.count}` : ''}
                  </span>
                ))}
              </div>
            )}
          </section>
        )}

        <FeatureCards data={plan} loading={loading && !plan}
                      onOpen={(next) => setTab(next)} />

        <section className={s.panel}>
          <div className={s.tabs}>
            {TABS.map((t) => (
              <button key={t.id} className={tab === t.id ? s.tabActive : s.tab}
                      onClick={() => setTab(t.id)}>
                {t.label}{t.ref && <span className={s.tabRef}>{t.ref}</span>}
              </button>
            ))}
          </div>

          <div style={{ paddingTop: 16 }}>
            {tab === 'plan' && (
              <Actions data={plan} currency={plan?.currency} onDecide={onDecide}
                       onApply={onApply} onUndo={onUndo}
                       onApproveMany={onApproveMany} busy={busy} />
            )}
            {OWNER_OF_TAB[tab] && (
              <>
                {tab === 'events' && watch?.actions?.length > 0 && (
                  <p className={s.principle}>{watch.note}</p>
                )}
                <Actions data={listFor(OWNER_OF_TAB[tab])} currency={plan?.currency}
                         onDecide={onDecide} onApply={onApply} onUndo={onUndo}
                         onApproveMany={onApproveMany} busy={busy} />
              </>
            )}
            {tab === 'planner' && (
              <>
                <Envelope plan={plan} currency={plan?.currency} />
                <Actions data={listFor('ceiling')} currency={plan?.currency}
                         onDecide={onDecide} onApply={onApply} onUndo={onUndo}
                         onApproveMany={onApproveMany} busy={busy} />
              </>
            )}
            {tab === 'changes' && (
              <Changes changes={changes} loading={changes === null}
                       onUndo={onUndo} busy={busy} />
            )}
            {tab === 'settings' && (
              <Settings settings={settings} plan={plan} onSave={onSaveSettings}
                        busy={busy} currency={plan?.currency} />
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>Budget Optimiser</h1></div>
          <p>Keep the month inside your budget, and stop good campaigns switching off
             early</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
